import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ShoppingCart, Package, Image as ImageIcon } from 'lucide-react';
import { Button, Input, Card } from '@caruso/ui';
import { storeService, StoreProduct } from '@/services/store.service';
import { productsService } from '@/services/products.service';
import { useCart } from '@/contexts/cart-context';
import { StoreLayout } from '@/components/layouts/store-layout';

// Componente para exibir imagem do produto com fallback
function ProductImage({ src, alt }: { src?: string; alt: string }) {
  const [imageError, setImageError] = useState(false);
  const hasImage = src && !imageError;

  return (
    <div className="w-full h-full flex items-center justify-center bg-brand-smoke/20">
      {hasImage ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          onError={() => setImageError(true)}
        />
      ) : (
        <ImageIcon className="w-16 h-16 text-brand-gold/40" />
      )}
    </div>
  );
}

export function StorePage() {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['store-products', search, category, minPrice, maxPrice, page],
    queryFn: () =>
      storeService.getProducts({
        search: search || undefined,
        category: category || undefined,
        minPrice: minPrice ? parseFloat(minPrice) : undefined,
        maxPrice: maxPrice ? parseFloat(maxPrice) : undefined,
        page,
        limit: 12,
      }),
  });

  const { data: categories } = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => productsService.getCategories(),
  });

  console.log('Store data:', data);
  console.log('Store loading:', isLoading);
  console.log('Store error:', error);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const handleAddToCart = (product: StoreProduct) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images.find((img) => img.isPrimary)?.url || product.images[0]?.url,
      stock: product.stock,
    });
  };

  return (
    <StoreLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-serif text-brand-gold">Loja</h1>
            <p className="text-brand-ivory/60 mt-1">
              Confira nossos produtos e serviços
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <div className="p-4 space-y-4">
            {/* Search */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
                <Input
                  placeholder="Buscar produtos..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Category Filter */}
              <div>
                <label className="block text-brand-ivory text-sm mb-2">Categoria</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-md text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                >
                  <option value="">Todas</option>
                  {categories?.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Min Price Filter */}
              <div>
                <label className="block text-brand-ivory text-sm mb-2">Preço mínimo</label>
                <Input
                  type="number"
                  placeholder="R$ 0,00"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
              </div>

              {/* Max Price Filter */}
              <div>
                <label className="block text-brand-ivory text-sm mb-2">Preço máximo</label>
                <Input
                  type="number"
                  placeholder="R$ 99999,00"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
              </div>
            </div>

            {/* Clear Filters */}
            {(search || category || minPrice || maxPrice) && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('');
                    setCategory('');
                    setMinPrice('');
                    setMaxPrice('');
                  }}
                >
                  Limpar filtros
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Products Grid */}
        {isLoading ? (
          <div className="text-center py-12 text-brand-ivory/50">
            Carregando produtos...
          </div>
        ) : data?.data.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-brand-gold/40 mx-auto mb-4" />
            <p className="text-brand-ivory/50">
              {search ? 'Nenhum produto encontrado' : 'Nenhum produto disponível no momento'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {data?.data.map((product) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all overflow-hidden h-full flex flex-col">
                    {/* Image */}
                    <div
                      className="aspect-square bg-brand-ink overflow-hidden cursor-pointer"
                      onClick={() => navigate(`/loja/produto/${product.id}`)}
                    >
                      <ProductImage
                        src={product.images.find((img) => img.isPrimary)?.url || product.images[0]?.url}
                        alt={product.name}
                      />
                    </div>

                    {/* Content */}
                    <div className="p-4 flex-1 flex flex-col">
                      <h3
                        className="font-serif text-brand-gold text-lg mb-2 cursor-pointer hover:underline"
                        onClick={() => navigate(`/loja/produto/${product.id}`)}
                      >
                        {product.name}
                      </h3>

                      {product.description && (
                        <p className="text-sm text-brand-ivory/60 mb-3 line-clamp-2">
                          {product.description}
                        </p>
                      )}

                      <div className="mt-auto">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-2xl font-bold text-brand-gold">
                            {formatPrice(product.price)}
                          </p>
                          {product.stock !== undefined && product.stock !== null && (
                            <p className="text-xs text-brand-ivory/50">
                              {product.stock > 0 ? `${product.stock} disponível` : 'Esgotado'}
                            </p>
                          )}
                        </div>

                        <Button
                          className="w-full"
                          onClick={() => handleAddToCart(product)}
                          disabled={product.stock !== undefined && product.stock !== null && product.stock <= 0}
                          variant="default"
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Adicionar ao carrinho
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {data && data.meta.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <span className="px-4 text-brand-ivory">
                  Página {page} de {data.meta.totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(data.meta.totalPages, p + 1))}
                  disabled={page === data.meta.totalPages}
                >
                  Próxima
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </StoreLayout>
  );
}
